import { useEffect, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export type AuthState = {
  loading: boolean;
  session: Session | null;
  user: User | null;
  isAdmin: boolean;
};

/**
 * Stays in `loading: true` until BOTH:
 *   - getSession() has resolved (hydration), AND
 *   - the role lookup for that session has completed.
 *
 * Pitfall fix: the onAuthStateChange listener fires INITIAL_SESSION with
 * `null` on a hard refresh before the cookie/local-storage session is
 * picked up. We never demote to signed-out from INITIAL_SESSION until
 * after the initial getSession() promise resolves.
 */
export function useAuthSession(): AuthState {
  const [state, setState] = useState<AuthState>({
    loading: true,
    session: null,
    user: null,
    isAdmin: false,
  });

  useEffect(() => {
    let cancelled = false;
    let hydrated = false;

    const resolveAdmin = async (session: Session | null): Promise<boolean> => {
      if (!session?.user) return false;
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id)
        .eq("role", "admin")
        .maybeSingle();
      if (error) {
        console.warn("useAuthSession: role lookup failed", error.message);
        return false;
      }
      return !!data;
    };

    const applySession = async (session: Session | null) => {
      const isAdmin = await resolveAdmin(session);
      if (cancelled) return;
      setState({
        loading: false,
        session,
        user: session?.user ?? null,
        isAdmin,
      });
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Defer ALL supabase calls out of the callback to avoid deadlocks.
      // Suppress INITIAL_SESSION null before hydration to prevent
      // a transient signed-out flash on hard refresh.
      if (event === "INITIAL_SESSION" && !hydrated && !session) return;
      setTimeout(() => { void applySession(session); }, 0);
    });

    supabase.auth.getSession().then(({ data }) => {
      hydrated = true;
      void applySession(data.session);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  return state;
}
